import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  XMarkIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { fetchCurrentUser } from '../../api';
import writerIcon from '../../img/icons/prosecaptain-writer.svg';
import assistantIcon from '../../img/icons/prosecaptain-assistant.svg';
import SidebarBottomControls from './SidebarBottomControls';

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  navigation,
  classNames,
  teams,
  Cog6ToothIcon,
  userNavigation,
  trialBannerHeight = 0,
  appLauncherLabel = 'Apps',
  activeAppMode = 'writer',
  isNotificationsDrawerOpen = false,
  onToggleNotificationsDrawer,
  onSignOut
}) => {
  const [username, setUsername] = useState('');
  const [lastName, setLastName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const modeIcon = activeAppMode === 'agent' ? assistantIcon : writerIcon;
  const modeIconAlt = activeAppMode === 'agent' ? 'AI Assistant' : 'AI Writer';
  const modeLabel = activeAppMode === 'agent' ? 'AI Assistant' : 'AI Writer';
  const modeHomeHref = activeAppMode === 'agent' ? '/agent/dashboard' : '/writer/dashboard';
  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem('directus_token');
      if (!token) return;

      try {
        const userData = await fetchCurrentUser([
          'first_name',
          'last_name',
          'linked_workspace.workspace_name',
          'trial_ends',
          'subscription_status',
          'subscription_plan'
        ]);
        if (!userData) return;
        setUsername(userData.first_name || '');
        setLastName(userData.last_name || '');
        setWorkspaceName(userData.linked_workspace?.workspace_name || '');
        localStorage.setItem('username', userData.first_name || '');
        localStorage.setItem('lastname', userData.last_name || '');
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);

  // Removed calculateDaysRemaining - no longer used

  return (
    <>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-gray-900" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col bg-neutral-100 px-6 pb-4 border-r border-gray-200">
                  <nav className="flex flex-1 flex-col min-h-0">
                    <ul className="flex flex-1 flex-col gap-y-7 min-h-0">
                      <li className="flex flex-1 flex-col min-h-0">
                        <ul className="-mx-2 space-y-1 flex-1 overflow-y-auto">
                          <li>
                            <a
                              href={modeHomeHref}
                              className="group flex flex-col items-center justify-center rounded-md p-0 gap-1"
                              title={modeIconAlt}
                            >
                              <img src={modeIcon} alt={modeIconAlt} className="h-11 w-11 object-contain" />
                              <span className="text-[11px] text-gray-500 tracking-wider font-semibold text-center leading-tight max-w-[90px]">
                                {modeLabel}
                              </span>
                            </a>
                          </li>
                          {navigation.map((item, index) => {
                            if (item.type === 'divider') {
                              return (
                                <li key={`divider-${index}`} className="my-2">
                                  <div className="border-t border-gray-200"></div>
                                </li>
                              );
                            }
                            return (
                              <li key={item.name}>
                                <a
                                  href={item.href}
                                  className={classNames(
                                    item.current
                                      ? 'bg-white text-gray-900 ring-1 ring-neutral-200 shadow-sm'
                                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50',
                                    'group flex gap-x-3 rounded-md p-2.5 text-sm leading-6 font-medium transition-all duration-200 ease-in-out'
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      item.current ? 'text-gray-700' : 'text-gray-600 group-hover:text-gray-700',
                                      'h-5 w-5 shrink-0 transition-colors duration-200 ease-in-out'
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </a>
                              </li>
                            );
                          })}
                          <li className="my-2">
                            <div className="border-t border-gray-200"></div>
                          </li>
                          <li>
                            <a
                              href="/"
                              className="group flex gap-x-3 rounded-md p-2.5 text-sm leading-6 font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200 ease-in-out"
                            >
                              <Squares2X2Icon
                                className="h-5 w-5 shrink-0 text-gray-600 group-hover:text-gray-700 transition-colors duration-200 ease-in-out"
                                aria-hidden="true"
                              />
                              {appLauncherLabel}
                            </a>
                          </li>
                        </ul>
                        <div className="mt-auto pt-4 border-t border-gray-200">
                          <SidebarBottomControls
                            layout="row"
                            isNotificationsDrawerOpen={isNotificationsDrawerOpen}
                            onToggleNotificationsDrawer={onToggleNotificationsDrawer}
                            userNavigation={userNavigation}
                            username={username}
                            lastName={lastName}
                            workspaceName={workspaceName}
                            onSignOut={onSignOut}
                          />
                        </div>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <div 
        className="hidden lg:fixed lg:left-0 lg:z-30 lg:flex lg:flex-col lg:w-[75px]"
        style={{
          top: `${trialBannerHeight}px`,
          height: `calc(100vh - ${trialBannerHeight}px)`
        }}
      >
        <div className="flex h-full w-full flex-col items-center bg-neutral-100 border-r border-gray-200 pt-2 pb-4">
          <div className="flex flex-col items-center gap-1">
            <a href={modeHomeHref} title={modeIconAlt} className="flex flex-col items-center gap-1">
              <img src={modeIcon} alt={modeIconAlt} className="h-11 w-11 object-contain" />
              <span className="text-[11px] text-gray-500 tracking-wider font-semibold text-center leading-tight max-w-[65px]">
                {modeLabel}
              </span>
            </a>
          </div>
          <div className="flex flex-1 flex-col items-center gap-4 overflow-y-auto w-full py-2 min-h-0">
          {/* Navigation */}
          {navigation.map((item, index) => {
            if (item.type === 'divider') {
              return (
                <div key={`divider-${index}`} className="w-8 border-t border-gray-200 my-2"></div>
              );
            }
            return (
              <div key={item.name} className="flex flex-col items-center gap-1">
                <a
                  href={item.href}
                  className={classNames(
                    'w-11 h-11 rounded-xl flex items-center justify-center transition-all relative',
                    item.current
                      ? 'bg-white text-gray-900 shadow-sm ring-1 ring-neutral-200'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  )}
                  title={item.name}
                >
                  <item.icon
                    className="h-6 w-6"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                </a>
                <span className="text-[11px]  text-gray-500  tracking-wider font-semibold text-center leading-tight max-w-[65px]">
                  {item.name}
                </span>
              </div>
            );
          })}
          <div className="w-8 border-t border-gray-200 my-2"></div>
          <div className="flex flex-col items-center gap-1">
            <a
              href="/"
              className="w-11 h-11 rounded-xl flex items-center justify-center transition-all relative text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              title={appLauncherLabel}
            >
              <Squares2X2Icon
                className="h-6 w-6"
                strokeWidth={1.5}
                aria-hidden="true"
              />
            </a>
            <span className="text-[11px]  text-gray-500  tracking-wider font-semibold text-center leading-tight max-w-[65px]">
              {appLauncherLabel}
            </span>
          </div>
          </div>
          <div className="mt-auto flex flex-col items-center gap-3 pt-3 border-t border-gray-200 w-full">
            <SidebarBottomControls
              isNotificationsDrawerOpen={isNotificationsDrawerOpen}
              onToggleNotificationsDrawer={onToggleNotificationsDrawer}
              userNavigation={userNavigation}
              username={username}
              lastName={lastName}
              workspaceName={workspaceName}
              onSignOut={onSignOut}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;